/**
 * UploadProgress - Real-time file upload progress display
 * Shows status of async background jobs with animated transitions
 */
import React from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileText,
  X,
  Clock,
  Zap
} from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';

const JOB_STATUS = {
  queued: { label: 'Queued', color: 'text-[#A0A0A0]', bg: 'bg-[#2F2F2F]', Icon: Clock },
  processing: { label: 'Processing', color: 'text-blue-400', bg: 'bg-blue-500/20', Icon: Loader2 },
  completed: { label: 'Complete', color: 'text-emerald-400', bg: 'bg-emerald-500/20', Icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/20', Icon: AlertTriangle },
};

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function JobItem({ job }) {
  const config = JOB_STATUS[job.status] || JOB_STATUS.queued;
  const { Icon } = config;
  const isActive = job.status === 'queued' || job.status === 'processing';

  return (
    <div
      className="p-3 bg-[#0D0D0D] rounded-lg border border-[#1F1F1F] transition-all"
      data-testid={`job-${job.jobId}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-[#6B6B6B] shrink-0" />
          <span className="text-sm text-white truncate" data-testid={`job-filename-${job.jobId}`}>
            {job.fileName}
          </span>
          <span className="text-xs text-[#6B6B6B] shrink-0">
            {formatBytes(job.fileSize)}
          </span>
        </div>
        <Badge className={`${config.bg} ${config.color} border-0 shrink-0`} data-testid={`job-status-${job.jobId}`}>
          <Icon className={`w-3 h-3 mr-1 ${isActive ? 'animate-spin' : ''}`} />
          {config.label}
        </Badge>
      </div>

      {/* Progress bar for active jobs */}
      {isActive && (
        <div className="space-y-1">
          <Progress value={job.progress || 0} className="h-1.5" />
          <div className="flex justify-between">
            <span className="text-xs text-[#6B6B6B]">{job.message || 'Processing...'}</span>
            <span className="text-xs text-[#6B6B6B]">{job.progress || 0}%</span>
          </div>
        </div>
      )}

      {/* Result summary for completed jobs */}
      {job.status === 'completed' && job.result && (
        <div className="flex items-center gap-3 text-xs text-[#A0A0A0] mt-1">
          {job.result.text_extracted && (
            <span>{job.result.character_count?.toLocaleString()} chars extracted</span>
          )}
          {job.result.embedded && (
            <span className="text-emerald-400">Indexed</span>
          )}
        </div>
      )}

      {/* Error message for failed jobs */}
      {job.status === 'failed' && job.error && (
        <p className="text-xs text-red-400 mt-1">
          {typeof job.error === 'string' ? job.error : job.error.message || 'Processing failed'}
        </p>
      )}
    </div>
  );
}

export default function UploadProgress({ jobs, onClear }) {
  if (!jobs || jobs.length === 0) return null;

  const completed = jobs.filter(j => j.status === 'completed').length;
  const failed = jobs.filter(j => j.status === 'failed').length;
  const active = jobs.length - completed - failed;

  return (
    <div
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden"
      data-testid="upload-progress-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#1F1F1F]">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <h3 className="font-medium text-white text-sm">
            Processing Jobs
          </h3>
          <span className="text-xs text-[#6B6B6B]">
            {active > 0 && `${active} active`}
            {active > 0 && completed > 0 && ' · '}
            {completed > 0 && `${completed} done`}
            {failed > 0 && ` · ${failed} failed`}
          </span>
        </div>
        {active === 0 && onClear && (
          <button
            onClick={onClear}
            className="text-xs text-[#6B6B6B] hover:text-white flex items-center gap-1 transition-colors"
            data-testid="clear-completed-btn"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {/* Job list */}
      <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
        {jobs.map(job => (
          <JobItem key={job.jobId} job={job} />
        ))}
      </div>
    </div>
  );
}
